import {
  ActionList, ActionListItem,
  AlertVariant,
  Bullseye, Button, Card,
  CardBody,
  Checkbox,
  DatePicker,
  EmptyState,
  EmptyStateActions, EmptyStateBody, EmptyStateFooter, Form, FormGroup,
  Grid, GridItem, Icon, PageSection, Pagination, PaginationVariant,
  SearchInput, Split, SplitItem, TextInput, Title, Toolbar,
  ToolbarContent, ToolbarItem, Tooltip
} from '@patternfly/react-core';
import { PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

// Custom Hooks & Utils
import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { RemoveButton } from '@app/components/RemoveButton';

import { useEntity } from '@app/hooks/useEntity';
import { Country } from '@app/model/Country';
import { Holiday } from '@app/model/Holiday';
import { useCountryStore, useHolidayAllRegionsStore } from '@app/store/entityStore';
import { toApiError } from '@app/utils/ApiError';
import { dateFormater, ddMMyyyyParse, fromIsoDate, toIsoDate } from '@app/utils/functions';
import { isSubdivisionOf, subdivisionName, subdivisionsOf, useSubdivisions } from '@app/utils/subdivisions';
import { TypeaheadSelect } from '@patternfly/react-templates';
import { DataLoadErrorRow } from '@app/components/DataLoadError';

const Holidays: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  // --- Core State ---
  // The all-regions store, not the one the rest of the application reads: this screen manages
  // holidays, so it has to show the regional days it can create. See useHolidayAllRegionsStore.
  const { items: holidays, error, retry } = useEntity(useHolidayAllRegionsStore);
  const saveHoliday = useHolidayAllRegionsStore(state => state.save);
  const deleteHoliday = useHolidayAllRegionsStore(state => state.delete);

  const { items: countries } = useEntity(useCountryStore);

  // Names for the ISO 3166-2 codes a region is stored as. Fetched as its own chunk, so the first
  // render of this page shows codes and the next shows names - see @app/utils/subdivisions.
  const subdivisions = useSubdivisions();
  
  // --- Form State ---
  const [formData, setFormData] = React.useState<Partial<Holiday>>({} as Partial<Holiday>);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isNew, setIsNew] = React.useState(false);

  // --- UI State ---
  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('holidays', 10);
  const [filterText, setFilterText] = React.useState('');
  
  // Filter State
  const [selectedCountryFilter, setSelectedCountryFilter] = React.useState<string | null>(null);
  const [selectedYearFilter, setSelectedYearFilter] = React.useState<string | null>(null);
  // Asking for every subdivision's days is what makes this list several times the size the rest
  // of the application sees, so it comes with a way back to the days a whole country observes.
  const [nationwideOnly, setNationwideOnly] = React.useState(false);

  const clearAllFilters = () => {
    setFilterText('');
    setSelectedCountryFilter(null);
    setSelectedYearFilter(null);
    setNationwideOnly(false);
    setPage(1);
  };

  /** What a region reads as: its name where one is known, and the code itself where it is not. */
  const regionLabel = React.useCallback(
    (region: string | null) => (region ? subdivisionName(region, subdivisions) : ''),
    [subdivisions]
  );

  /** The regions the form offers: the selected country's, and whatever the record already holds. */
  const regionOptions = React.useMemo(() => {
    const options = subdivisionsOf(formData.country?.alpha2, subdivisions).map(s => ({
      // The code alongside the name because the importer writes codes: an administrator
      // checking a row against what it produced searches for one as readily as the other.
      content: `${s.name} (${s.code})`,
      value: s.code,
      selected: s.code === formData.region
    }));

    // A code the shipped names do not cover - newer than the CLDR release they were generated
    // from, or one the service invented - still has to show as the current selection rather
    // than leave the picker looking empty.
    if (formData.region && !options.some(o => o.value === formData.region)) {
      options.unshift({ content: formData.region, value: formData.region, selected: true });
    }

    return options;
  }, [formData.country?.alpha2, formData.region, subdivisions]);

  // --- Derived State: Dynamic Country Filter List ---
  const availableFilterCountries = React.useMemo(() => {
    // Extract unique countries that actually exist in the currently loaded holidays
    const uniqueCountries = new Map<number, Country>();
    holidays.forEach(h => {
      if (h.country && h.country.id) {
        uniqueCountries.set(h.country.id, h.country);
      }
    });
    return Array.from(uniqueCountries.values());
  }, [holidays]);

  const availableFilterYears = React.useMemo(() => {
    // fromIsoDate, not new Date: a holiday stored as "2026-01-01" parses as midnight UTC,
    // which in a negative offset lands on 31 December and files New Year under the wrong year.
    const years = new Set<string>(holidays.map(h => fromIsoDate(h.date).getFullYear().toString()));
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [holidays]);

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo(() => {
    const needle = filterText.trim().toLowerCase();

    return holidays.filter(h => {
      // The code as well as the name: the importer writes codes, so an administrator checking
      // what it produced searches for "ES-AN" as readily as for "Andalusia".
      const textMatch = !needle ||
        h.description?.toLowerCase().includes(needle) ||
        (h.country && h.country.name.toLowerCase().includes(needle)) ||
        (h.region ? h.region.toLowerCase().includes(needle) || regionLabel(h.region).toLowerCase().includes(needle) : false);

      const countryMatch = !selectedCountryFilter || 
        (h.country && h.country.id.toString() === selectedCountryFilter);

      // NEW: Year Match
      const yearMatch = !selectedYearFilter ||
        fromIsoDate(h.date).getFullYear().toString() === selectedYearFilter;

      const regionMatch = !nationwideOnly || !h.region;

      return textMatch && countryMatch && yearMatch && regionMatch;
    });
  }, [holidays, filterText, selectedCountryFilter, selectedYearFilter, nationwideOnly, regionLabel]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // --- Form Handlers ---
  const handleFieldChange = (field: keyof Holiday, value: any) => {
    setFormData(prev => {
      const newState = { ...prev, [field]: value };

      // A region belongs to one country - ES-AN is not Germany's to store - so changing the
      // country drops whatever was picked under the old one rather than saving a mismatch.
      if (field === 'country') {
        newState.region = null;
      }
      return newState;
    });
  };

  const onNewClick = () => {
    // Nationwide unless a region is picked, which is what every holiday was before the service
    // started carrying regional ones.
    setFormData({ region: null } as Partial<Holiday>);
    setIsEditing(true);
    setIsNew(true);
  };

  const onEditClick = (holiday: Holiday) => {
    setFormData(holiday);
    setIsEditing(true);
    setIsNew(false);
  };

  const onCancelClick = () => {
    setFormData({} as Partial<Holiday>);
    setIsEditing(false);
    setIsNew(false);
  };

  // --- CRUD Operations ---
  const onSaveClick = async () => {
    if (!formData.description || !formData.date || !formData.country) {
      addNotification('Validation Error', AlertVariant.warning, 'Description, Date and Country are required.');
      return;
    }

    // Typed rather than picked, most likely: the list offers this country's subdivisions, but a
    // code newer than the names shipped with the bundle has to be enterable, and a typed one can
    // belong to the wrong country or not be a subdivision code at all.
    if (formData.region && !isSubdivisionOf(formData.region, formData.country.alpha2)) {
      addNotification(
        'Validation Error',
        AlertVariant.warning,
        `"${formData.region}" is not a region of ${formData.country.name}. Regions are ISO 3166-2 codes, such as ${formData.country.alpha2}-AN.`
      );
      return;
    }

    beginLoading();
    try {
      await saveHoliday(formData, isNew);
      
      addNotification(
        isNew ? 'Holiday Created' : 'Holiday Updated', 
        AlertVariant.success, 
        `${formData.description} was successfully ${isNew ? 'added' : 'updated'}.`
      );

      setIsEditing(false);
      setIsNew(false);
      setFormData({} as Partial<Holiday>);
    } catch (error: any) {
      // The service refuses a second holiday on the same date for the same country and region,
      // which is worth saying in those words: the generic message names a constraint rather than
      // the row the reader is looking at, and the row it collides with may be a regional one
      // they cannot see under the filters they have on.
      const message = toApiError(error).code === 409
        ? `${formData.country?.name ?? 'That country'} already has a holiday on ${formatDateForDisplay(formData.date!)}${
            formData.region ? ` for ${regionLabel(formData.region)}` : ' nationwide'
          }.`
        : error.message;

      addNotification('Error saving holiday', AlertVariant.danger, message);
    } finally {
      endLoading();
    }
  };

  const onRemoveConfirm = async (id: string | number) => {
    beginLoading();
    try {
      await deleteHoliday(id);
      addNotification('Holiday Removed', AlertVariant.info, 'The holiday has been deleted.');
      
      if (formData.id === id) {
        onCancelClick();
      }
    } catch (error: any) {
      addNotification('Error deleting holiday', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  // --- UI Helpers ---
  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact={true}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  const formatDateForDisplay = (dateStr: string | Date) => {
    if (!dateStr) return '';
    // A holiday is a calendar day, not an instant: read the stored "yyyy-MM-dd" off the
    // local calendar so it is not shown as the day before.
    const date = typeof dateStr === 'string' ? fromIsoDate(dateStr) : dateStr;
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  };

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Holidays</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem hidden={!isEditing}>
                <Button variant="secondary" onClick={onCancelClick}>Cancel</Button>
              </ActionListItem>
              <ActionListItem hidden={!isEditing}>
                <Button variant="primary" onClick={onSaveClick}>Save</Button>
              </ActionListItem>
              <ActionListItem hidden={isEditing}>
                <Button variant="primary" onClick={onNewClick}>New Holiday</Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <PageSection hasBodyWrapper isWidthLimited>
        <Card isPlain className='form-same-list-page'>
          <CardBody>
            <Form>
              <Grid hasGutter md={3}>
                
                <GridItem>
                  <FormGroup label="Description" isRequired fieldId="description">
                    <TextInput 
                      isRequired 
                      type="text" 
                      id="description" 
                      name="description" 
                      value={formData.description || ''} 
                      onChange={(_e, val) => handleFieldChange('description', val)} 
                      isDisabled={!isEditing} 
                      placeholder="e.g. New Year's Day" 
                    />
                  </FormGroup>
                </GridItem>
                
                <GridItem>
                  <FormGroup label="Date" isRequired fieldId="date">
                    {/* dd/MM/yyyy in the field, "yyyy-MM-dd" in the record: the format
                        pair has to be given explicitly, or the input renders in one format
                        and parses in another. The value is only replaced once a real date
                        comes back, so a half-typed one is not wiped mid-keystroke. */}
                    <DatePicker
                      id="date"
                      value={formData.date ? dateFormater(fromIsoDate(formData.date)) : ''}
                      onChange={(_e, val, date) => {
                        if (date && !isNaN(date.getTime())) handleFieldChange('date', toIsoDate(date));
                        else if (!val) handleFieldChange('date', '');
                      }}
                      dateFormat={dateFormater}
                      dateParse={ddMMyyyyParse}
                      placeholder="DD/MM/YYYY"
                      isDisabled={!isEditing}
                      appendTo={() => document.body}
                    />
                  </FormGroup>
                </GridItem>

                <GridItem>
                  <FormGroup label="Country" isRequired fieldId="country">
                    {/* The option's value is the country's id, which is what onSelect looks it
                        up by. They have to agree: keyed on anything else the lookup misses and
                        the form saves with no country at all. */}
                    <TypeaheadSelect
                      initialOptions={countries.map(c => ({
                        content: `${c.flag} ${c.name}`,
                        value: c.id,
                        selected: c.id === formData.country?.id
                      }))}
                      key={`ctry-${formData.id || 'new'}`} 
                      placeholder="Select a country" id='country'
                      noOptionsFoundMessage={(filter) => `No country was found for "${filter}"`}
                      onClearSelection={() => { handleFieldChange('country', null) }}
                      onSelect={(_ev, v) => handleFieldChange('country', countries.find((c) => c.id === v as number))}
                      isDisabled={!isEditing}
                    />
                  </FormGroup>
                </GridItem>

                {/* Region: which subdivision observes the day, or none of them - see the note on
                    Holiday.region. Cleared means nationwide, which is what the field is left at
                    for all but the handful of countries that publish regional holidays. */}
                <GridItem>
                  <FormGroup label="Region" fieldId="region">
                    <TypeaheadSelect
                      initialOptions={regionOptions}
                      // Remounted when the country changes as well as when the record does: the
                      // options belong to a country, and the text left in the input would
                      // otherwise still name the old one's subdivision.
                      key={`region-${formData.id || 'new'}-${formData.country?.id ?? 'none'}`}
                      placeholder={formData.country ? 'Nationwide' : 'Select a country first'}
                      id='region'
                      // A subdivision newer than the names shipped with the bundle has to be
                      // enterable, or the screen cannot manage a holiday the importer created.
                      isCreatable
                      createOptionMessage={(code) => `Use "${code.trim().toUpperCase()}"`}
                      noOptionsAvailableMessage={`No regions are known for ${formData.country?.name ?? 'this country'}`}
                      noOptionsFoundMessage={(filter) => `No region was found for "${filter}"`}
                      onClearSelection={() => handleFieldChange('region', null)}
                      onSelect={(_ev, v) => handleFieldChange('region', String(v).trim().toUpperCase())}
                      isDisabled={!isEditing || !formData.country}
                    />
                  </FormGroup>
                </GridItem>

              </Grid>
            </Form>
          </CardBody>
        </Card>
      </PageSection>

      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            <div style={{ opacity: isEditing ? 0.4 : 1, pointerEvents: isEditing ? 'none' : 'auto', transition: 'opacity 0.3s ease-in-out' }}>
              <Toolbar id="compact-toolbar" clearAllFilters={clearAllFilters}>
                <ToolbarContent>
                  
                  {/* Text Search */}
                  <ToolbarItem>
                    <SearchInput 
                      aria-label="Search holidays" 
                      placeholder="Search description, country or region..." 
                      value={filterText} 
                      onChange={(_e, val) => { setFilterText(val); setPage(1); }} 
                      onClear={() => setFilterText('')} 
                    />
                  </ToolbarItem>

                  {/* Dynamic Country Filter Dropdown */}
                  <ToolbarItem style={{ minWidth: '220px' }}>
                    <TypeaheadSelect
                      id="country-filter"
                      initialOptions={availableFilterCountries.map(c => ({ content: `${c.flag} ${c.name}`, value: c.id.toString() }))}
                      placeholder="Filter by country..."
                      noOptionsFoundMessage={(filter: string) => `No country was found for "${filter}"`}
                      onClearSelection={() => {
                        setSelectedCountryFilter(null);
                        setPage(1);
                      }}
                      onSelect={(_ev: any, v: string | number) => {
                        setSelectedCountryFilter(v.toString());
                        setPage(1);
                      }}
                    />
                  </ToolbarItem>

                  {/* Year Filter */}
                  <ToolbarItem style={{ minWidth: '160px' }}>
                    <TypeaheadSelect
                      id="year-filter"
                      initialOptions={availableFilterYears.map(year => ({ content: year, value: year }))}
                      placeholder="Filter by year..."
                      onClearSelection={() => {
                        setSelectedYearFilter(null);
                        setPage(1);
                      }}
                      onSelect={(_ev: any, v: string | number) => {
                        setSelectedYearFilter(v.toString());
                        setPage(1);
                      }}
                    />
                  </ToolbarItem>

                  {/* The way back to the list this screen used to show. Asking the service for
                      every subdivision's days is what makes this one several times the size. */}
                  <ToolbarItem>
                    <Checkbox
                      id="nationwide-only"
                      label="Nationwide only"
                      isChecked={nationwideOnly}
                      onChange={(_e, checked) => { setNationwideOnly(checked); setPage(1); }}
                    />
                  </ToolbarItem>

                  <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
                </ToolbarContent>
              </Toolbar>
              
              {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
                  gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
              <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0 }}>
                <InnerScrollContainer>
                  <Table aria-label="Holidays Table" isStickyHeader variant='compact' gridBreakPoint="">
                    <Thead>
                      <Tr>
                        <Th>Description</Th>
                        <Th>Date</Th>
                        <Th>Country</Th>
                        <Th>Region</Th>
                        <Th width={10} isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedRows.map((row) => (
                        <Tr key={row.id} isRowSelected={formData.id === row.id}>
                          <Td dataLabel="Description"><b>{row.description}</b></Td>
                          <Td dataLabel="Date">{formatDateForDisplay(row.date)}</Td>
                          <Td dataLabel="Country">
                            {row.country ? `${row.country.flag} ${row.country.name}` : ''}
                          </Td>
                          <Td dataLabel="Region">
                            {/* Null is the ordinary case and reads as such. A code with no name
                                behind it shows as itself rather than twice over. */}
                            {!row.region ? (
                              <span style={{ color: 'var(--pf-t--global--text--color--subtle, #6a6e73)' }}>Nationwide</span>
                            ) : regionLabel(row.region) === row.region ? (
                              row.region
                            ) : (
                              <React.Fragment>
                                {regionLabel(row.region)}{' '}
                                <span style={{ fontSize: 'var(--pf-t--global--font--size--sm, 12px)', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)' }}>{row.region}</span>
                              </React.Fragment>
                            )}
                          </Td>
                          <Td dataLabel="Actions" modifier="nowrap"
                            isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                            <ActionList>
                              <ActionListItem>
                                <Tooltip content="Edit">
                                  <Button 
                                    icon={<Icon size='lg'><PencilAltIcon /></Icon>} 
                                    variant="plain" 
                                    onClick={() => onEditClick(row)} 
                                    isDisabled={isEditing} 
                                  />
                                </Tooltip>
                              </ActionListItem>
                              <ActionListItem>
                                <RemoveButton 
                                  confirmationMessage={`Remove ${row.description}?`} 
                                  onRemoveConfirm={() => onRemoveConfirm(row.id)} 
                                  isDisabled={isEditing} 
                                />
                              </ActionListItem>
                            </ActionList>
                          </Td>
                        </Tr>
                      ))}
                      {/* Nothing to show: distinguish "the load failed" from "no records match" */}
                      {error && filteredRows.length === 0 && (
                        <DataLoadErrorRow colSpan={5} error={error} entityLabel="holidays" onRetry={retry} />
                      )}
                      {!error && filteredRows.length === 0 && (
                        <Tr>
                          <Td colSpan={5}>
                            <Bullseye>
                              <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No results found">
                                <EmptyStateBody>No holidays match your search criteria.</EmptyStateBody>
                                <EmptyStateFooter>
                                  <EmptyStateActions>
                                    <Button variant="link" onClick={clearAllFilters}>
                                      Clear search
                                    </Button>
                                  </EmptyStateActions>
                                </EmptyStateFooter>
                              </EmptyState>
                            </Bullseye>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </InnerScrollContainer>
              </OuterScrollContainer>
              {buildPagination('bottom')}
            </div>
          </CardBody>
        </Card>
      </PageSection>
    </React.Fragment>
  );
};

export { Holidays };
